import { useEffect, useState, useCallback, useRef } from "react";
import { UiComponents } from "@simulanisindia/ui";
import Slider from "@mui/material/Slider";
import { useDispatch, useSelector } from "react-redux";
import { AudioPrev, CloseIcon, DeleteIcon, MinusIcon, PDFPrev, PlusIcon, ThumbIcon } from "../../../assets/icons/icons";
import pdfIcon from "../../../assets/img/pdf.png";
import { AUDIO, HOTSPOT, IMAGE, PDF, SCENE, VIDEO, MEDIA, LINK } from "../../../constant/constant";
import LibraryPopup from "../../../components/modals/LibraryPopup";
import { getImageDimensions } from "../../../utils/general";
import IconPopup from "../../../components/modals/IconsPopup";
import { DEFAULT_HOTSPOT_ACTION } from "../../../utils/constant";
import panorma from "../../../assets/img/mediaoptions.png";
import mediaOptionScene from "../../../assets/img/mediaOptionsScene.png";

const { DropdownSearch, SimInputBar, SimToggleSwitch } = UiComponents;

let hotspotInventoryType = [
  { value: LINK, label: LINK },
  { value: MEDIA, label: MEDIA },
];

const Functions = ({
  currentHotspot,
  setCurrentHotspot,
  scene,
  setScene,
  activeTab = null,
  animateToHotspotPosition,
}) => {
  const { sceneList } = useSelector((state) => state.projectSlice);
  const [showHotspotFile, setShowHotspotFile] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [size, setSize] = useState(0.2);
  const [showHotspotIconModal, setShowHotspotIconModal] = useState(false);
  const debounceTimerRef = useRef(null);

  const [activeIcon, setActiveIcon] = useState({
    DETAIL: false,
    SCENE_DEFAULT_VIEW: false,
    SCENE_VIDEO_CONTROL: false,
    HOTSPOT_MEDIA: false,
    HOTSPOT_LINK: false,
    HOTSPOT_SCALE: false,
    HOTSPOT_TIMER: false,
  });
  const [currentCameraView, setCurrentCameraView] = useState({ x: 0, y: 0, z: 0 });
  const [cameraInputValues, setCameraInputValues] = useState({ x: 0, y: 0, z: 0 });

  const savePOV = () => {
    setScene((pre) => ({
      ...pre,
      cameraView: currentCameraView,
    }));
  };
  // Debounced function to update camera position
  const debouncedPanToView = useCallback(
    (position) => {
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {

        // Validate that all values are valid numbers (including negative)
        const isValidX = typeof position.x === "number" && !isNaN(position.x);
        const isValidY = typeof position.y === "number" && !isNaN(position.y);
        const isValidZ = typeof position.z === "number" && !isNaN(position.z);


        if (isValidX && isValidY && isValidZ) {
          animateToHotspotPosition(position);
        }
      }, 500); // Wait 500ms after user stops typing
    },
    [animateToHotspotPosition]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Function to get current camera view from Three.js scene
  const getCurrentCameraView = () => {
    if (window._threeDViewerCamera) {
      const camera = window._threeDViewerCamera;
      const position = camera.position;
      const newPosition = {
        x: Math.round(position.x * 1000) / 1000,
        y: Math.round(position.y * 1000) / 1000,
        z: Math.round(position.z * 1000) / 1000,
      };
      setCurrentCameraView(newPosition);
      setCameraInputValues(newPosition);
    }
  };

  // Update camera view when component mounts or scene changes
  useEffect(() => {
    getCurrentCameraView();

    // Set up interval to update camera view periodically
    const interval = setInterval(getCurrentCameraView, 1000);

    return () => clearInterval(interval);
  }, [scene?.uuid]); // Update when scene changes

  const findImageDimensions = async (path) => {
    return await getImageDimensions(path);
  };

  const updateScaleProperty = async (path) => {
    let { width, height } = await findImageDimensions(path);
    setSize(currentHotspot?.scale?.x / width);
  };

  useEffect(() => {
    if (!currentHotspot?.inventory?.path) return;
    updateScaleProperty(currentHotspot?.inventory?.path);
  }, [currentHotspot]);

  const handleUpdateScaleSize = async (_size) => {
    setSize(_size);
    let { width, height } = await findImageDimensions(currentHotspot?.inventory?.path);
    setCurrentHotspot((pre) => ({
      ...pre,
      scale: {
        ...pre.scale,
        x: width * parseFloat(_size),
        y: height * parseFloat(_size),
      },
    }));
  };

  // Handle camera input changes with validation
  const handleCameraInputChange = (axis, value) => {
    const newInputValues = { ...cameraInputValues, [axis]: value };
    setCameraInputValues(newInputValues);

    // Allow empty string for better UX while typing
    if (value === "" || value === "-") {
      return;
    }

    // Try to parse as float and update if valid
    const parsedValue = parseFloat(value);

    if (!isNaN(parsedValue)) {
      const newPosition = { ...currentCameraView, [axis]: parsedValue };
      debouncedPanToView(newPosition);
    }
  };

  const handleChangeValue = (key, value) => {
    setScene((pre) => ({
      ...pre,
      [key]: value,
    }));
  };
  const handleChangeHotspotPosition = (key, value) => {
    setCurrentHotspot((pre) => ({
      ...pre,
      [key]: value,
    }));
  };

  const handleSelectScene = (e) => {
    setCurrentHotspot((pre) => ({
      ...pre,
      destination: e.value,
    }));
  };

  const handleToggle = (type) => {
    setActiveIcon((pre) => ({
      ...pre,
      [type]: !pre[type],
    }));
  };

  const handleHotspotChangeValue = (key, value) => {
    const _value = value?.length === 0 ? null : value;
    setCurrentHotspot((pre) => ({
      ...pre,
      action: [
        {
          ...pre.action[0],
          [key]: _value,
        },
      ],
    }));
  };

  const handleChangeScene = (item) => {
    setScene((pre) => ({
      ...pre,
      inventory: {
        uuid: item.uuid,
        path: item.path,
        type: item.fileType,
      },
    }));
    setTimeout(() => setShowChangeModal(false), 1000);
  };

  const handleChangeHotspotFile = (item) => {
    setCurrentHotspot((pre) => ({
      ...pre,
      action: [
        {
          ...pre.action[0],
          inventory: {
            uuid: item.uuid,
            path: item.path,
            type: item.fileType,
          },
        },
      ],
    }));
    setTimeout(() => setShowHotspotFile(false), 1000);
  };

  const handleChangeHotspotType = (e) => {
    setCurrentHotspot((pre) => ({
      ...pre,
      type: e.value,
      destination: null,
      action: [DEFAULT_HOTSPOT_ACTION],
    }));
  };

  const handleChangeHotspotTimer = (e) => {
    setCurrentHotspot((pre) => ({
      ...pre,
      timer: { start: parseInt(e.target.value) },
    }));
  };

  const handleHotspotTitleChange = (e) => {
    const value = e?.length === 0 ? null : e;
    setCurrentHotspot((pre) => ({
      ...pre,
      name: value,
    }));
  };

  useEffect(() => {
    setActiveIcon({
      DETAIL: true,
      SCENE_DEFAULT_VIEW: false,
      SCENE_VIDEO_CONTROL: false,
      HOTSPOT_MEDIA: false,
      HOTSPOT_LINK: false,
      HOTSPOT_SCALE: false,
      HOTSPOT_TIMER: false,
    });
  }, [activeTab]);

  const handleChangeHotspot = (item) => {
    let currentHotspotId = currentHotspot.uuid;

    let inventory = {
      path: item.path,
      type: item.fileType,
      uuid: item.uuid,
    };

    setCurrentHotspot((pre) => ({
      ...pre,
      inventory,
    }));

    setScene((prevScene) => {
      const updatedHotspots = prevScene.hotspots.map((hotspot) => {
        if (hotspot.uuid === currentHotspotId) {
          return {
            ...hotspot,
            inventory,
          };
        }
        return hotspot;
      });

      return {
        ...prevScene,
        hotspots: updatedHotspots,
      };
    });

    setTimeout(() => setShowHotspotIconModal(false), 1000);
  };

  const handleChangeHotspotIcon = () => {
    setShowHotspotIconModal(true);
  };

  const handleRemoveHotspotInventory = () => {
    setCurrentHotspot((pre) => ({
      ...pre,
      action: [
        {
          ...pre.action[0],
          inventory: null,
        },
      ],
    }));

    setTimeout(() => setShowHotspotFile(false), 1000);
  };

  let hotspotLinkFileType = currentHotspot?.action?.[0]?.inventory?.type || null;

  return (
    <>
      <div className="toolbarFunctions">
        <div className="tabData">
          {activeTab === SCENE && (
            <div className="hotspotsDetails sceneDetails">
              <div className={`mediaItems pb-0 ${activeIcon["DETAIL"] ? "active" : ""}`}>
                <div className="itemHead" onClick={() => handleToggle("DETAIL")}>
                  <h6>Basic Details</h6>
                  <span className="openClose">{!activeIcon["DETAIL"] ? <PlusIcon /> : <MinusIcon />}</span>
                </div>
                <div className="inputItem-wrapper">
                  <span className="uploadBox">
                    {/* Show the below div when nothing is selected */}
                    {/* <div className="uploadimgthreesixty forScene" onClick={() => setShowHotspotFile(true)}>
                            <img src={mediaOptionScene} alt="Upload 360 media" />
                            <h6>Click here to upload media</h6>
                          </div> */}
                    {scene?.inventory?.type === "IMAGE" ? (
                      <img src={scene?.inventory?.path} referrerPolicy="no-referrer" />
                    ) : (
                      <video
                        autoPlay={false}
                        controls={false}
                        src={scene?.inventory?.path}
                        referrerPolicy="no-referrer"
                      />
                    )}
                    <span className="replaceBtn" title="Replace Media" onClick={() => setShowChangeModal(true)}>
                      Replace
                    </span>
                  </span>
                  <div className="inputItem">
                    <p>Title</p>
                    <SimInputBar
                      name="name"
                      placeholder="Enter Title"
                      value={scene?.name || ""}
                      onChange={(e) => handleChangeValue(e.target.name, e.target.value)}
                    />
                  </div>

                  <div className="inputItem ">
                    <p>Description</p>
                    <div className="sim-input">
                      <textarea
                        className="form-control"
                        name="description"
                        value={scene?.description || ""}
                        placeholder="Description"
                        onChange={(e) => handleChangeValue(e.target.name, e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={`inputItems pb-0 ${activeIcon["SCENE_DEFAULT_VIEW"] ? "active" : ""}`}>
                <div className="itemHead" onClick={() => handleToggle("SCENE_DEFAULT_VIEW")}>
                  <h6>Default view</h6>
                  <span className="openClose">{activeIcon["SCENE_DEFAULT_VIEW"] ? <MinusIcon /> : <PlusIcon />}</span>
                </div>

                <div className="inputItem-wrapper">
                  <div className="inputItem ">
                    <p>Current View</p>
                  </div>
                  <div className="inputItem forPOV">
                    <div className="povOptions">
                      <div className="povItems">
                        <p>X : </p>
                        <SimInputBar
                          placeholder="X"
                          name="x"
                          value={cameraInputValues.x}
                          onChange={(e) => handleCameraInputChange("x", e.target.value)}
                        />
                      </div>
                      <div className="povItems">
                        <p>Y : </p>
                        <SimInputBar
                          placeholder="Y"
                          name="y"
                          value={cameraInputValues.y}
                          onChange={(e) => handleCameraInputChange("y", e.target.value)}
                        />
                      </div>
                      <div className="povItems">
                        <p>Z : </p>
                        <SimInputBar
                          placeholder="Z"
                          name="z"
                          value={cameraInputValues.z}
                          onChange={(e) => handleCameraInputChange("z", e.target.value)}
                        />
                      </div>
                    </div>
                    <span onClick={savePOV} className="savePov smallSave w-100 mt-2">
                      Save POV as Default
                    </span>
                  </div>
                </div>
              </div>

              {scene?.inventory?.type === "VIDEO" && (
                <div className={`inputItems pb-0 ${activeIcon["SCENE_VIDEO_CONTROL"] ? "active" : ""}`}>
                  <div className="itemHead" onClick={() => handleToggle("SCENE_VIDEO_CONTROL")}>
                    <h6>Video Control</h6>
                    <span className="openClose">
                      {activeIcon["SCENE_VIDEO_CONTROL"] ? <MinusIcon /> : <PlusIcon />}
                    </span>
                  </div>
                  <div className="inputItem-wrapper">
                    <div className="inputItem forPOV">
                      <div className="inputItem toggle">
                        <p>Video Loop</p>
                        <SimToggleSwitch
                          name="loop"
                          checked={scene?.loop}
                          onChange={(e) => handleChangeValue(e.target.name, !scene?.loop)}
                        />
                      </div>
                      <div className="inputItem toggle">
                        <p>Mute Video</p>
                        <SimToggleSwitch
                          name="isMuted"
                          checked={scene?.isMuted}
                          onChange={(e) => handleChangeValue(e.target.name, !scene?.isMuted)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === HOTSPOT && (
            <div className="hotspotsDetails">
              <div className={`inputItems pb-0 ${activeIcon["DETAIL"] ? "active " : ""}`}>
                <div className="itemHead" onClick={() => handleToggle("DETAIL")}>
                  <h6>Hotspot Details </h6>
                  <span className="openClose">{!activeIcon["DETAIL"] ? <PlusIcon /> : <MinusIcon />}</span>
                </div>

                <div className="inputItem-wrapper">
                  <div className="inputItem">
                    <p>Name</p>
                    <SimInputBar
                      placeholder="Enter hotspot name"
                      value={currentHotspot?.name}
                      onChange={(e) => handleHotspotTitleChange(e.target.value)}
                    />
                  </div>
                  <div className="inputItem">
                    <p>Type</p>
                    <DropdownSearch
                      selectProps={{
                        options: hotspotInventoryType,
                        value: hotspotInventoryType.find((e) => e.value === currentHotspot?.type),
                        onChange: (e) => handleChangeHotspotType(e),
                      }}
                    />
                  </div>
                  <div className="inputItem Icons">
                    <p> Select Icon </p>
                    <span className="iconPrev" onClick={handleChangeHotspotIcon}>
                      <img src={currentHotspot?.inventory?.path || ""} referrerPolicy="no-referrer" />
                      {/* <span className="cancel" onClick={() => setIconSelected(false)}> <CloseIcon /> </span> */}
                    </span>
                  </div>
                </div>
              </div>

              {currentHotspot?.type === MEDIA && (
                <div className={`mediaItems pb-0 ${activeIcon["HOTSPOT_MEDIA"] ? "active " : ""}`}>
                  <div className="itemHead" onClick={() => handleToggle("HOTSPOT_MEDIA")}>
                    <h6>Actions</h6>
                    <span className="openClose">{!activeIcon["HOTSPOT_MEDIA"] ? <PlusIcon /> : <MinusIcon />}</span>
                  </div>
                  <div className="inputItem-wrapper">
                    <div className="inputItem">
                      <p>Title</p>
                      <SimInputBar
                        name="title"
                        placeholder="Enter Title"
                        value={currentHotspot?.action?.[0]?.title || ""}
                        onChange={(e) => handleHotspotChangeValue(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="inputItem ">
                      <p>Description</p>
                      <div className="sim-input">
                        <textarea
                          className="form-control"
                          name="description"
                          value={currentHotspot?.action?.[0]?.description || ""}
                          placeholder="Description"
                          onChange={(e) => handleHotspotChangeValue(e.target.name, e.target.value)}
                        />
                      </div>
                    </div>
                    <span className="uploadBox">
                      {currentHotspot?.action?.[0]?.inventory?.path ? (
                        hotspotLinkFileType === IMAGE ? (
                          <img src={currentHotspot?.action?.[0]?.inventory?.path} referrerPolicy="no-referrer" />
                        ) : hotspotLinkFileType === VIDEO ? (
                          <video src={currentHotspot?.action?.[0]?.inventory?.path} width="100%" controls />
                        ) : (
                          <div className="svg-Prev">
                            {hotspotLinkFileType === PDF ? (
                              <PDFPrev />
                            ) : hotspotLinkFileType === AUDIO ? (
                              <AudioPrev />
                            ) : (
                              ""
                            )}
                          </div>
                        )
                      ) : (
                        <div className="uploadimgthreesixty" onClick={() => setShowHotspotFile(true)}>
                          <img src={panorma} alt="Upload 360 media" />
                          <h6>Click here to upload media</h6>
                        </div>
                      )}

                      {currentHotspot?.action?.[0]?.inventory?.path && (
                        <div className="replace-delete">
                          <span className="replaceBtn" title="Replace Media" onClick={() => setShowHotspotFile(true)}>
                            Replace
                          </span>
                          <span className="replaceBtn" title="Remove Media" onClick={handleRemoveHotspotInventory}>
                            <DeleteIcon />
                          </span>
                        </div>
                      )}
                    </span>

                    {/* <span className="uploadBox">
                      {hotspotLinkFileType === IMAGE ?
                        <img src={currentHotspot?.action?.[0]?.inventory?.path} referrerPolicy="no-referrer" />
                        :
                        hotspotLinkFileType === VIDEO ?
                          <video src={currentHotspot?.action?.[0]?.inventory?.path} width="100%" controls />
                          :
                          <div className="svg-Prev"> {hotspotLinkFileType === PDF ? <PDFPrev /> : hotspotLinkFileType === AUDIO ? <AudioPrev /> : ""} </div>
                      }
                      <div className="replace-delete">
                        <span className="replaceBtn" title="Replace Media" onClick={() => setShowHotspotFile(true)}>
                          Replace
                        </span>
                        {currentHotspot?.action?.[0]?.inventory?.path &&
                          <span className="replaceBtn" title="Remove Media" onClick={handleRemoveHotspotInventory}>
                            <DeleteIcon />
                          </span>}

                      </div>

                    </span> */}
                  </div>
                </div>
              )}

              {currentHotspot?.type === LINK && (
                <div className={`inputItems pb-0 ${activeIcon["HOTSPOT_LINK"] ? "active" : ""}`}>
                  <div className="itemHead" onClick={() => handleToggle("HOTSPOT_LINK")}>
                    <h6>Actions</h6>
                    <span className="openClose">{activeIcon["HOTSPOT_LINK"] ? <MinusIcon /> : <PlusIcon />}</span>
                  </div>
                  <div className="inputItem-wrapper">
                    <div className="inputItem ">
                      <p>Destination Scene</p>
                      <DropdownSearch
                        selectProps={{
                          options: sceneList
                            .filter((e) => e?.uuid != scene?.uuid)
                            .map((e) => ({ value: e.uuid, label: e.name })),
                          value:
                            sceneList
                              .filter((e) => e.uuid === currentHotspot?.destination)
                              .map((e) => ({ value: e.uuid, label: e.name }))[0] || null,
                          onChange: (e) => handleSelectScene(e),
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentHotspot?.inventory?.path && (
                <div className={`inputItems pb-0 ${activeIcon["HOTSPOT_SCALE"] ? "active" : ""}`}>
                  <div className="itemHead" onClick={() => handleToggle("HOTSPOT_SCALE")}>
                    <h6>Scale & Properties</h6>
                    <span className="openClose">{activeIcon["HOTSPOT_SCALE"] ? <MinusIcon /> : <PlusIcon />}</span>
                  </div>
                  <div className="inputItem-wrapper">
                    <div className="inputItem">
                      <p>Scale</p>
                      <Slider
                        value={size}
                        aria-label="Default"
                        valueLabelDisplay="auto"
                        onChange={(e) => handleUpdateScaleSize(e.target.value)}
                        min={0.05}
                        max={1}
                        step={0.05}
                      />
                    </div>
                    <div className="inputItem forPOV">
                      <div className="povOptions">
                        <div className="povItems">
                          <p>X : </p>
                          <SimInputBar
                            placeholder="X"
                            name="x"
                            value={currentHotspot?.position.x || ""}
                            onChange={(e) =>
                              handleChangeHotspotPosition("position", {
                                ...currentHotspot.position,
                                [e.target.name]: Number(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="povItems">
                          <p>Y : </p>
                          <SimInputBar
                            placeholder="Y"
                            name="y"
                            value={currentHotspot?.position.y || ""}
                            onChange={(e) =>
                              handleChangeHotspotPosition("position", {
                                ...currentHotspot.position,
                                [e.target.name]: Number(e.target.value),
                              })
                            }
                          />
                        </div>
                        <div className="povItems">
                          <p>Z : </p>
                          <SimInputBar
                            placeholder="Z"
                            name="z"
                            value={currentHotspot?.position.z || ""}
                            onChange={(e) =>
                              handleChangeHotspotPosition("position", {
                                ...currentHotspot.position,
                                [e.target.name]: Number(e.target.value),
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`inputItems pb-0 ${activeIcon["HOTSPOT_TIMER"] ? "active" : ""}`}>
                <div className="itemHead" onClick={() => handleToggle("HOTSPOT_TIMER")}>
                  <h6>
                    Timer & Visibility
                    <span className="betaTag">BETA</span>
                  </h6>
                  <span className="openClose">{activeIcon["HOTSPOT_TIMER"] ? <MinusIcon /> : <PlusIcon />}</span>
                </div>
                <div className="inputItem-wrapper">
                  <div className="inputItem single">
                    <p>Delay In Seconds</p>
                    <SimInputBar
                      placeholder="0"
                      value={parseInt(currentHotspot?.timer?.start) || null}
                      onChange={(e) => handleChangeHotspotTimer(e)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <LibraryPopup
        show={showChangeModal}
        selectedId={scene?.inventory?.uuid || null}
        handleClose={() => setShowChangeModal(false)}
        handleChangeScene={handleChangeScene}
        showTabs={[IMAGE, VIDEO]}
      />
      <IconPopup
        hotspotId={currentHotspot?.inventory?.uuid || null}
        show={showHotspotIconModal}
        handleClose={() => setShowHotspotIconModal(false)}
        handleChangeHotspot={handleChangeHotspot}
      />
      <LibraryPopup
        show={showHotspotFile}
        selectedId={currentHotspot?.action[0]?.inventory?.uuid || null}
        handleClose={() => setShowHotspotFile(false)}
        handleChangeScene={handleChangeHotspotFile}
      />
    </>
  );
};

export default Functions;
